// imports
import { pxToRem, styled } from '@withjoy/joykit/theme';
import { Flex, Box, TextV2, DrawerV2 } from '@withjoy/joykit';
import { ChevronDown, ChevronUp } from '@withjoy/joykit/icons';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';

// local imports
import { useTranslation } from '@shared/core';
import { withWindow } from '@shared/utils/withWindow';
import { animationTransition } from '@shared/utils/animationTransition';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';
import { useSupportedCurrencyOptions, SupportedCurrency } from '@shared/utils/currency';
import { updateLocaleCurrency, useGeoBasedLocale, CurrencyCode } from '@shared/hooks/useGeoLocale';

// interfaces
interface CurrencySelectorTriggerProps
  extends Readonly<{
    isOpen: boolean;
    onClick: () => void;
    currency?: CurrencyCode;
  }> {}

interface CurrencySelectorModalProps
  extends Readonly<{
    isOpen: boolean;
    onClose: () => void;
    triggerRef?: React.RefObject<HTMLDivElement>;
    trigger?: React.ReactNode;
  }> {}

/**
 * CurrencySelectorTrigger - Reusable trigger button for currency selector
 * @param isOpen - Whether the modal is currently open
 * @param onClick - Click handler for the trigger
 * @param currency - Optional currency code to display (defaults to current locale currency)
 */
export const CurrencySelectorTrigger = React.forwardRef<HTMLDivElement, CurrencySelectorTriggerProps>(({ isOpen, onClick, currency: currencyProp }, ref) => {
  const { tq } = useTranslation('stays');
  const { currency: localeCurrency } = useGeoBasedLocale();
  const currency = currencyProp ?? localeCurrency;

  const isMobile = useIsMobileScreen();

  const currencySymbol = useMemo(() => getCurrencySymbolFromCurrencyCode(currency), [currency]);

  return (
    <Flex
      ref={ref}
      color="mono14"
      gap={pxToRem(4)}
      cursor="pointer"
      onClick={onClick}
      width={pxToRem(100)}
      height={pxToRem(32)}
      alignItems="center"
      justifyContent="center"
      marginRight={pxToRem(16)}
      typographyVariant="button2"
      borderRadius={isMobile ? undefined : pxToRem(16)}
      backgroundColor={isMobile ? 'transparent' : 'mono2'}
      _hover={isMobile ? undefined : { backgroundColor: 'mono1' }}
    >
      {currencySymbol} {tq`${currency}`}
      {isOpen ? (
        <ChevronUp
          size="sm"
          color="mono14"
        />
      ) : (
        <ChevronDown
          size="sm"
          color="mono14"
        />
      )}
    </Flex>
  );
});

CurrencySelectorTrigger.displayName = 'CurrencySelectorTrigger';

/**
 * Custom dropdown controller hook - simplified to only return ref
 * @returns Object containing dropdownRef for click-outside detection
 */
const useDropdownController = () => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  return { dropdownRef };
};

/**
 * CurrencySelectorModal - Responsive currency selection component
 * Renders as dropdown on desktop and bottom sheet on mobile
 * @param isOpen - Whether the modal is currently open
 * @param onClose - Callback function to close the modal
 * @param triggerRef - Optional external reference to the trigger button (deprecated, use trigger prop instead)
 * @param trigger - Optional custom trigger component (modal will manage ref internally)
 */
const CurrencySelectorModal = ({ isOpen, onClose, triggerRef: externalTriggerRef, trigger }: CurrencySelectorModalProps) => {
  const { t } = useTranslation('stays');

  const { currency } = useGeoBasedLocale();
  const isMobile: boolean = useIsMobileScreen();
  const { dropdownRef } = useDropdownController();
  const allSupportedCurrencies = useSupportedCurrencyOptions();

  const internalTriggerRef = useRef<HTMLDivElement>(null);
  const triggerRef = externalTriggerRef ?? internalTriggerRef;

  const [selectedCurrencyCode, setSelectedCurrencyCode] = useState<Readonly<CurrencyCode>>(currency);

  const handleCurrencySelect = useCallback((currency: SupportedCurrency): void => {
    updateLocaleCurrency(currency.code);
    withWindow(global => global.location.reload());
  }, []);

  const handleMobileCurrencySelect = useCallback((currency: SupportedCurrency): void => {
    setSelectedCurrencyCode(currency.code);
  }, []);

  const handleSave = useCallback(() => {
    updateLocaleCurrency(selectedCurrencyCode);
    withWindow(global => global.location.reload());
    onClose();
  }, [selectedCurrencyCode, onClose]);

  // Initialize selected currency when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedCurrencyCode(currency);
    }
  }, [isOpen, currency]);

  // Handle click outside for desktop dropdown
  useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideTrigger = triggerRef?.current && !triggerRef.current.contains(target);
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target);

      if (isOutsideDropdown && isOutsideTrigger) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isMobile, onClose, dropdownRef, triggerRef]);

  /**
   * Memoized render function for currency items
   * Displays currency name and code with symbol
   * @param supportedCurrency - Currency object with name and code
   * @returns JSX element for currency display
   */
  const renderCurrencyItem = useCallback((supportedCurrency: SupportedCurrency & { name: string }) => {
    const symbol = getCurrencySymbolFromCurrencyCode(supportedCurrency.code);

    return (
      <Flex
        height="100%"
        flexDirection="column"
        justifyContent="center"
      >
        <TextV2
          color="mono14"
          marginBottom={pxToRem(2)}
          typographyVariant="button1"
        >
          {supportedCurrency.name}
        </TextV2>
        <TextV2
          typographyVariant="label1"
          color="mono10"
        >
          {supportedCurrency.code} - {symbol}
        </TextV2>
      </Flex>
    );
  }, []);

  const triggerElement = trigger ? <div ref={internalTriggerRef}>{trigger}</div> : null;

  // Desktop dropdown rendering
  if (!isMobile) {
    return (
      <>
        {triggerElement}
        {isOpen && (
          <DropdownWrapper ref={dropdownRef}>
            <DropdownContent>
              {allSupportedCurrencies.map(supportedCurrency => (
                <CurrencyItem
                  onClick={() => {
                    handleCurrencySelect(supportedCurrency);
                    onClose();
                  }}
                  key={supportedCurrency.code}
                  isSelected={supportedCurrency.code === currency}
                >
                  {renderCurrencyItem(supportedCurrency)}
                </CurrencyItem>
              ))}
            </DropdownContent>
          </DropdownWrapper>
        )}
      </>
    );
  }

  // Mobile DrawerV2 rendering
  return (
    <>
      {triggerElement}
      <DrawerV2
        size="auto"
        anchor="bottom"
        isOpen={isOpen}
        onClose={onClose}
        overrides={{
          Content: {
            props: {
              maxHeight: '90vh',
              minHeight: '60vh',
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
            },
          },
          Header: {
            props: {
              paddingY: 5,
              paddingX: 4,
            },
          },
          Body: {
            props: {
              flex: 1,
              paddingY: 3,
              paddingX: 7,
              overflowY: 'auto',
            },
          },
        }}
      >
        <DrawerV2.Header borderBottom="1px solid #EBEBEB">
          <TextV2
            textAlign="left"
            typographyVariant="hed4"
          >
            {t('currencySelectorModal', 'chooseACurrency')()}
          </TextV2>
          <DrawerV2.CloseButton />
        </DrawerV2.Header>

        <DrawerV2.Body>
          <StyledCurrencyOptionsContainer>
            {allSupportedCurrencies.map(supportedCurrency => (
              <StyledCurrencyOption
                isMobile={isMobile}
                key={supportedCurrency.code}
                onClick={() => handleMobileCurrencySelect(supportedCurrency)}
                isSelected={supportedCurrency.code === selectedCurrencyCode}
              >
                {renderCurrencyItem(supportedCurrency)}
              </StyledCurrencyOption>
            ))}
          </StyledCurrencyOptionsContainer>
        </DrawerV2.Body>

        <DrawerV2.Footer borderTop="1px solid #EBEBEB">
          <SaveButton
            width="100%"
            color="white"
            cursor="pointer"
            onClick={handleSave}
            height={pxToRem(48)}
            alignItems="center"
            justifyContent="center"
            backgroundColor="mono14"
            borderRadius={pxToRem(24)}
            typographyVariant="button1"
            _hover={{ backgroundColor: 'mono13' }}
          >
            {t('currencySelectorModal', 'save')()}
          </SaveButton>
        </DrawerV2.Footer>
      </DrawerV2>
    </>
  );
};

// Styled components for dropdown
const DropdownWrapper = styled.div`
  cursor: pointer;
  position: relative;
`;

const DropdownContent = styled(Box)`
  z-index: 1000;
  overflow-y: auto;
  position: absolute;
  top: ${pxToRem(50)};
  right: ${pxToRem(-40)};
  width: ${pxToRem(222)};
  height: ${pxToRem(400)};
  padding: ${pxToRem(12)};
  border-radius: ${pxToRem(8)};
  background-color: ${props => props.theme.colors.fillLight};
  box-shadow:
    0 7px 27px rgba(44, 41, 37, 0.06),
    0 4px 16px rgba(0, 0, 0, 0.07);
`;

// Consolidated currency item component for both desktop and mobile
const CurrencyItem = styled(Box)<{ isSelected: boolean; isMobile?: boolean }>`
  display: flex;
  cursor: pointer;
  height: ${pxToRem(59)};
  flex-direction: column;
  justify-content: center;
  border-radius: ${pxToRem(28)};
  padding: ${pxToRem(12)} ${pxToRem(24)};
  transition: ${animationTransition('background-color')};
  width: ${({ isMobile }) => (isMobile ? '100%' : pxToRem(198))};
  margin-bottom: ${({ isMobile }) => (isMobile ? 0 : pxToRem(4))};
  background-color: ${({ isSelected, theme }) => (isSelected ? theme.colors.mono2 : 'transparent')};
  &:hover {
    background-color: ${({ isSelected, theme }) => (isSelected ? theme.colors.mono2 : theme.colors.mono1)};
  }
  &:last-child {
    margin-bottom: 0;
  }
`;

// Styled components for mobile (DialogV2)
const StyledCurrencyOptionsContainer = styled(Box)`
  display: flex;
  gap: ${pxToRem(8)};
  flex-direction: column;
`;

// Alias for mobile usage to maintain clarity
const StyledCurrencyOption = CurrencyItem;

const SaveButton = styled(Flex)`
  transition: ${animationTransition('background-color')};
`;

export default CurrencySelectorModal;
